// ============================================================
// CannaSpy Map — shared types
// ============================================================
// Used by layer builders, data hooks, and all map pages.
// Keep this file the single source of truth for map-domain types.

export type LicenseType = 'retail' | 'delivery' | 'microbusiness'

export type MarketTier = 'standard' | 'competitive' | 'hot' | 'elite'

export type TrackState = 'untracked' | 'tracking' | 'blocked' | 'both'

/**
 * Canonical per-rival relationship flags — THE shape every surface
 * (pin inner-dot, sidebar row dot, popup badge, action buttons) derives
 * its display from. Always produced via trackStateToFlags so the four
 * surfaces are structurally incapable of disagreeing.
 */
export interface TrackBlockFlags {
  isTracked: boolean
  isBlocked: boolean
}

export function trackStateToFlags(ts?: TrackState | string | null): TrackBlockFlags {
  return {
    isTracked: ts === 'tracking' || ts === 'both',
    isBlocked: ts === 'blocked' || ts === 'both',
  }
}

export function flagsToTrackState(f: TrackBlockFlags): TrackState {
  if (f.isTracked && f.isBlocked) return 'both'
  if (f.isBlocked) return 'blocked'
  if (f.isTracked) return 'tracking'
  return 'untracked'
}

/** A single competitor point rendered on the map (legacy / setup flow). */
export interface CompetitorFeatureProps {
  id: string
  name: string
  license_type: LicenseType
  address?: string
  dcc_license?: string
  /** Market-heat score 0–1 — drives choropleth and heatmap intensity. */
  heat_score?: number
  market_tier?: MarketTier
  /** Relationship to the currently selected org. */
  track_state?: TrackState
  /** Median price of a reference SKU in this zip (for hover detail). */
  median_price?: number | null
}

/**
 * Live dispensary pin — comes from /api/v1/map/dispensaries GeoJSON.
 * Three visual states driven by track_state + enriched:
 *   BLOCKED   → amber  (track_state === 'blocked')
 *   ENRICHED  → tier-color (enriched === true)
 *   PROSPECT  → dim grey (default)
 *
 * NOTE: dcc_license is NULL for all rows — statewide ingest is 100% primary-platform data.
 */
export interface DispensaryFeatureProps {
  id: string
  dcc_license: string
  name: string
  city: string
  county: string
  license_type: LicenseType
  business_type: string | null
  market_tier: MarketTier | null
  enriched: boolean
  threat_score: number | null
  price_observations_count: number
  last_scraped_at: string | null
  track_state: TrackState
  /** Operator group key (2B) — a store's storefront + delivery pins share
   *  it. Track state propagates across the operator's pins. */
  operator_id?: string | null
  /** Brand group key (2B) — all of a chain's operators share it. Block
   *  state propagates across every pin of the brand. */
  brand_id?: string | null
  /** Competitor row ids the server matched to this dispensary's OPERATOR
   *  (csd-id / dcc_license / name join in map.ts — both pins of a store
   *  resolve the same rows). The ONLY dispensary→competitor bridge the
   *  client uses — no client-side re-matching.
   *  NOTE: queryRenderedFeatures JSON-stringifies array props; normalize
   *  with parseCompetitorIds before use. */
  competitor_ids?: string[] | string
  /** Competitor ids holding the org's active block whose BRAND reach covers
   *  this pin — unblock must target these slot rows (the block may live on
   *  a brand sibling, not this pin's own competitor). */
  block_competitor_ids?: string[] | string
  /** True when another org has an active block on this dispensary. */
  blocked_by_competitor?: boolean
  /** SERVER-COMPUTED, org-relative: true when this dispensary is the requesting
   *  org's OWN store (its storefront OR its delivery listing — they share
   *  operator_id). Resolved server-side via the org's own
   *  location.platform_slug → dispensaries.platform_slug → operator_id chain,
   *  so it catches the delivery listing the ~75m coordinate check misses.
   *  SOURCE OF TRUTH for the own-store popup gate; the isOwnLocation()
   *  coordinate helper stays only as a fallback when this is undefined (older
   *  cached tiles / a listing with a null operator_id). */
  is_own?: boolean
}

/** queryRenderedFeatures returns array props as JSON strings — normalize. */
export function parseCompetitorIds(raw: DispensaryFeatureProps['competitor_ids']): string[] {
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string' && raw.startsWith('[')) {
    try { return JSON.parse(raw) as string[] } catch { return [] }
  }
  return []
}

/** Zip-code polygon feature for the choropleth. */
export interface ZipHeatFeatureProps {
  zip: string
  /** Count of active delivery operators covering this zip. */
  delivery_operators: number
  /** Count of retail storefronts inside this zip. */
  storefronts: number
  /** Weighted density score 0–1. */
  density: number
  market_tier?: MarketTier
}

/** Viewport state — controlled from the parent. */
export interface MapViewport {
  longitude: number
  latitude: number
  zoom: number
  bearing?: number
  pitch?: number
}

/** Default viewport — centered on California, full-state view. */
export const CALIFORNIA_VIEWPORT: MapViewport = {
  longitude: -119.4179,
  latitude: 36.7783,
  zoom: 5.4,
  bearing: 0,
  pitch: 0,
}

/** Zoom breakpoints — keep layer visibility consistent across the app. */
export const ZOOM = {
  /** Statewide choropleth only. */
  STATE: 5,
  /** County/zip fills dominant, clusters start appearing. */
  REGION: 7,
  /** Clusters + zip fills. */
  METRO: 9,
  /** Individual markers revealed. */
  NEIGHBORHOOD: 11,
  /** Detail zoom — delivery polygon overlays, street-level pins. */
  DETAIL: 13,
} as const
